import { Box, Button, Dialog, Grid, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useState } from "react";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader";
import DialogButtons from "@entrilia/shared/components/DialogButtons";

type ChoiceType = "copy" | "override";

interface Props {
  onContinue: (override: boolean) => void;
  onCancel: () => void;
}

export function ReportOverridePrompt({ onCancel, onContinue }: Props) {
  const [choice, setChoice] = useState<ChoiceType>("copy");

  return (
    <Dialog
      open
      hideBackdrop
      slotProps={{
        paper: {
          sx: {
            p: 0.5,
            width: 555,
            height: 360,
            boxShadow:
              "0px 3px 3px -2px rgba(16, 24, 40, 0.20), 0px 3px 4px 0px rgba(16, 24, 40, 0.14), 0px 1px 8px 0px rgba(16, 24, 40, 0.12)",
          },
        },
      }}
    >
      <DialogHeader text="Reports with duplicated names" onClose={onCancel} />
      <Grid container sx={{ px: 2.5, py: 1, width: "100%" }}>
        <Typography color={"secondary"}>
          Reports with identical names have been identified. How would you like to proceed?
        </Typography>
      </Grid>
      <Grid container sx={{ px: 2.5, pt: 1.5, flex: 1, width: "100%" }}>
        <RadioGroup sx={{ flex: 1, gap: 1.5 }} value={choice} onChange={(_, value) => setChoice(value as ChoiceType)}>
          <CopyItem selectedChoice={choice} onClick={() => setChoice("copy")} />
          <OverrideItem selectedChoice={choice} onClick={() => setChoice("override")} />
        </RadioGroup>
      </Grid>
      <DialogButtons onCancel={onCancel}>
        <Button variant="contained" onClick={() => onContinue(choice === "override")}>
          Continue
        </Button>
      </DialogButtons>
    </Dialog>
  );
}

interface ItemProps {
  selectedChoice: ChoiceType;
  onClick: () => void;
}

function CopyItem(props: ItemProps) {
  return (
    <ItemWrapper value="copy" {...props}>
      <Typography variant="subtitle2">Copy with new name</Typography>
      <Typography color={"secondary"}>The reports will be copied with a modified name.</Typography>
      <Stack direction={"row"} spacing={0.5}>
        <Typography color={"secondary"}>Example :</Typography>
        <Typography>Investor Contributions/Distributions (1)</Typography>
      </Stack>
    </ItemWrapper>
  );
}

function OverrideItem(props: ItemProps) {
  return (
    <ItemWrapper value="override" {...props}>
      <Typography variant="subtitle2">Rewrite report</Typography>
      <Typography color={"secondary"}>
        The report will be replaced, and any prior configuration will be erased.
      </Typography>
      <Typography color={"secondary"}>
        If you don&apos;t have permission to override a report, a copy with a new name will be created instead.
      </Typography>
    </ItemWrapper>
  );
}

interface ItemWrapperProps extends ItemProps {
  value: ChoiceType;
}

function ItemWrapper({ value, selectedChoice, onClick, children }: PropsWithChildren<ItemWrapperProps>) {
  const active = selectedChoice === value;

  return (
    <Grid
      container
      sx={{
        pl: 1.5,
        pr: 1,
        py: 1,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        alignItems: "center",
        ...(active && { borderColor: "primary.main" }),
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      <Stack sx={{ flex: 1 }}>{children}</Stack>
      <Box>
        <Radio value={value} />
      </Box>
    </Grid>
  );
}
